import { useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Fab,
  IconButton,
  Paper,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";

import SendIcon from "@mui/icons-material/Send";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { useTheme } from "@mui/material/styles";
import { formatDate } from "../lib/utils";
import { API_BASE_URL } from "../../../../config";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { showSnackbar } from "../../../../redux/slices/snackbar";

export default function ChatComponent({ canSendMessages }) {
  const maxMessageLength = 250;
  const theme = useTheme();

  const [messages, setMessages] = useState([]);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleSnackbar = (message, severity) => {
    dispatch(showSnackbar({ message, severity }));
  };

  const [input, setInput] = useState("");
  const { idUid } = useParams();
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const scrollRef = useRef(null);
  const isDarkMode = theme.palette.mode === "dark";

  const loggedInUserId = sessionStorage.getItem("uid");
  const nome = sessionStorage.getItem("nome");
  const cognome = sessionStorage.getItem("cognome");

  const scrollToBottom = () => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleScroll = () => {
    if (!scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 5;
    setShowScrollBtn(!isAtBottom);
  };

  const groupMessagesByDate = (messages) => {
    const groups = [];
    messages?.forEach((msg) => {
      const datePart = msg.dataMessaggio
        ? msg.dataMessaggio.split(" ")[0]
        : "Senza Data";
      let lastGroup = groups[groups.length - 1];

      if (!lastGroup || lastGroup.date !== datePart) {
        groups.push({
          date: datePart,
          messages: [msg],
        });
      } else {
        lastGroup.messages.push(msg);
      }
    });
    return groups;
  };

  const groupedMessages = groupMessagesByDate(messages);

  const mainBg = isDarkMode ? "#1B2635" : "#efe7de";
  const bubbleOthers = isDarkMode ? "#263548" : "#ffffff";
  const bubbleSelf = isDarkMode ? "#005c4b" : "#e3f2fd";
  const textColor = isDarkMode ? "#ffffff" : "#111b21";
  const textSecondary = isDarkMode ? "#8696a0" : "#667781";
  const doodleOpacity = isDarkMode ? 0.05 : 0.4;

  const fetchMessages = async () => {
    const token = sessionStorage.getItem("token");
    if (!idUid || !token) return;

    try {
      const requestOptions = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + token,
        },
      };

      const response = await fetch(
        `${API_BASE_URL}/api/issue/list/${idUid}`,
        requestOptions,
      );
      const res = await response.json();

      if (res.status === -1) {
        handleSnackbar(res.message, "error");
        sessionStorage.clear();
        navigate("/auth/sign-in");
        return;
      } else if (res.status === 0) {
        handleSnackbar(res.message, "error");
        navigate("/private");
        return;
      } else {
        setMessages(res.data);
      }
    } catch (e) {
      console.log("Error fetching messages:", e);
      handleSnackbar(e.message, "error");
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const handleSend = async () => {
    if (!input.trim() || input.length > maxMessageLength) return;
    const token = sessionStorage.getItem("token");

    try {
      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + token,
        },
        body: JSON.stringify({
          data: { message: input.trim() },
        }),
      };

      const response = await fetch(
        `${API_BASE_URL}/api/issue/${idUid}/messages`,
        requestOptions,
      );
      const res = await response.json();

      if (res.status === -1) {
        handleSnackbar(res.message, "error");
        sessionStorage.clear();
        navigate("/auth/sign-in");
        return;
      } else if (res.status === 0) {
        handleSnackbar(res.message, "error");
        return;
      }
      fetchMessages();
    } catch (e) {
      console.log(e);
      handleSnackbar(e.message, "error");
    }
    setInput("");
  };

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        height: isDesktop ? 500 : 400,
        display: "flex",
        flexDirection: "column",
        position: "relative",
        bgcolor: mainBg,
        backgroundImage: isDarkMode
          ? "none"
          : "url('https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-8e1d-9bc08c520845.png')",
        overflow: "hidden",
        border: isDarkMode ? "1px solid #2a3942" : "1px solid #d1d7db",
        borderRadius: "0 0 8px 8px",
        "&::before": isDarkMode
          ? {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage:
                "url('https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-8e1d-9bc08c520845.png')",
              opacity: doodleOpacity,
              pointerEvents: "none",
            }
          : {},
      }}
    >
      {/* Chat History */}
      <Box
        ref={scrollRef}
        onScroll={handleScroll}
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          p: 2,
          pb: 8,
          zIndex: 1,
        }}
      >
        {groupedMessages.map((group, groupIndex) => (
          <Box
            key={group.date}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            {/* Date Header */}
            <Box
              sx={{
                alignSelf: "center",
                bgcolor: isDarkMode ? "rgba(11, 20, 26, 0.7)" : "#d1f4ff",
                px: 2,
                py: 0.5,
                borderRadius: 2,
                my: 2,
                boxShadow: "0 1px 0.5px rgba(0,0,0,0.13)",
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  color: isDarkMode ? "#8696a0" : "#54656f",
                  textTransform: "uppercase",
                }}
              >
                {group.date}
              </Typography>
            </Box>

            {group.messages.map((msg, msgIndex) => {
              const isSelf = msg.idUIDUserId === loggedInUserId;
              console.log(loggedInUserId, msg.idUIDUserId);
              const senderName = `${msg.nome} ${msg.cognome}`;
              const timePart = msg.dataMessaggio
                ? msg.dataMessaggio.split(" ")[1]?.substring(0, 5) || ""
                : "";
              const userColor = isSelf
                ? isDarkMode
                  ? "#00a884"
                  : "#1976d2"
                : msg.idUIDUserId === ""
                ? "#ff3c00ff" // Orange for system/Mobility Express (idUIDUserId "")
                : "#00a884";

              return (
                <Box
                  key={msg.id || `${groupIndex}-${msgIndex}`}
                  sx={{
                    display: "flex",
                    flexDirection: isSelf ? "row-reverse" : "row",
                    alignItems: "start",
                    mb: 1,
                    gap: 0.5,
                    alignSelf: isSelf ? "flex-end" : "flex-start",
                  }}
                >
                  <Avatar
                    sx={{
                      width: 28,
                      height: 28,
                      fontSize: "0.7rem",
                      bgcolor: userColor,
                      fontWeight: 600,
                      mb: 0.5, // small lift to align with bubble base
                    }}
                  >
                    {msg.nomeAvatar}
                  </Avatar>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      maxWidth: "85%",
                      minWidth: "80px",
                      bgcolor: isSelf ? bubbleSelf : bubbleOthers,
                      borderRadius: isSelf ? "8px 8px 0 8px" : "8px 8px 8px 0",
                      px: 1.5,
                      py: 0.8,
                      boxShadow: "0 1px 0.5px rgba(0,0,0,0.13)",
                      position: "relative",
                      borderLeft: isSelf ? "none" : `3px solid ${userColor}`,
                    }}
                  >
                    {!isSelf && (
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 700,
                          color: userColor,
                          mb: 0.2,
                        }}
                      >
                        {senderName}
                      </Typography>
                    )}
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        justifyContent: "space-between",
                        alignItems: "flex-end",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: textColor,
                          lineHeight: 1.5,
                          mr: 4,
                          fontSize: "0.9rem",
                        }}
                      >
                        {msg.messaggio}
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: "0.65rem",
                          color: textSecondary,
                          ml: "auto",
                          mt: 0.5,
                          textAlign: "right",
                        }}
                      >
                        {timePart}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>

      {/* Scroll to bottom button */}
      {showScrollBtn && (
        <Fab
          size="small"
          onClick={scrollToBottom}
          sx={{
            position: "absolute",
            bottom: 80,
            right: 20,
            bgcolor: isDarkMode ? "#202c33" : "white",
            color: isDarkMode ? "#aebac1" : "#54656f",
            "&:hover": { bgcolor: isDarkMode ? "#2a3942" : "#f0f2f5" },
            boxShadow:
              "0 1px 1px 0 rgba(11,20,26,.06),0 2px 5px 0 rgba(11,20,26,.2)",
            zIndex: 2,
          }}
        >
          <ArrowDownwardIcon fontSize="small" />
        </Fab>
      )}

      {/* Input Area */}
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          p: "10px 16px",
          alignItems: "center",
          borderTop: isDarkMode ? "1px solid #3f4e57ff" : "1px solid #d1d7db",
          bgcolor: isDarkMode ? "none" : "#f0f2f5",
          zIndex: 2,
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Scrivi un messaggio"
          value={input}
          onChange={(e) => {
            if (e.target.value.length <= maxMessageLength) {
              setInput(e.target.value);
            }
          }}
          onKeyDown={(e) => e.key === "Enter" && handleSend()}
          helperText={`${input.length}/${maxMessageLength}`}
          FormHelperTextProps={{
            sx: {
              textAlign: "right",
              color:
                input.length >= maxMessageLength ? "error.main" : "inherit",
              mt: 1,
              fontSize: "0.65rem",
              fontWeight: 600,
            },
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              bgcolor: isDarkMode ? "#233044" : "white",
              color: isDarkMode ? "#d1d7db" : "black",
              borderRadius: "8px",
              "& fieldset": { border: "none" },
            },
            "& .MuiInputBase-input::placeholder": {
              color: isDarkMode ? "#8696a0" : "inherit",
              opacity: 1,
            },
          }}
        />
        <IconButton
          onClick={handleSend}
          disabled={!input.trim() || canSendMessages}
          sx={{
            color: "#407ad6",
            "&.Mui-disabled": { color: isDarkMode ? "#3b4a54" : "#acbac2" },
          }}
        >
          <SendIcon />
        </IconButton>
      </Box>
    </Paper>
  );
}
